import type { ListingSummary } from '../../services/adminApi'
import { cdnImage } from '../../utils/cdnImage'

interface ListingCardProps {
  listing: ListingSummary
  selected?: boolean
  onSelect?: (id: string) => void
  onClick?: (listing: ListingSummary) => void
}

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  pending: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  approved: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  rejected: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-400' },
  removed: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400' },
  deleted: { bg: 'bg-gray-200 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
  sold: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-400' },
  draft: { bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400' },
}

export function ListingCard({ listing, selected, onSelect, onClick }: ListingCardProps) {
  const formattedPrice = new Intl.NumberFormat('en-ET', {
    style: 'currency',
    currency: listing.currency,
    minimumFractionDigits: 0,
  }).format(listing.price)

  const formattedDate = new Date(listing.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const status = statusColors[listing.status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' }

  return (
    <div
      role="button"
      tabIndex={0}
      className={`group relative rounded-2xl border bg-white dark:bg-[#1a1520] p-4 transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1 shadow-sm dark:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503864] dark:focus-visible:ring-[#b58fcc] ${
        selected ? 'border-[#503864] dark:border-[#7f4f9a] ring-2 ring-[#503864]/20 dark:ring-[#7f4f9a]/20' : 'border-[#ecdefc] dark:border-[#3d2c4f]'
      }`}
      onClick={() => onClick?.(listing)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(listing)
        }
      }}
    >
      <div className="relative flex gap-4">
        {onSelect && (
          <div className="flex items-start pt-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation()
                onSelect(listing.id)
              }}
              className="h-4 w-4 rounded-md border-2 border-[#d0b8df] dark:border-[#503864] text-[#503864] dark:text-[#7f4f9a] focus:ring-[#503864] dark:focus:ring-[#7f4f9a] focus:ring-offset-0 transition-colors"
            />
          </div>
        )}

        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-[#f9f4ff] dark:bg-[#3d2c4f] ring-1 ring-[#f0e3ff] dark:ring-[#503864]">
          {listing.image_url ? (
            <img
              src={cdnImage(listing.image_url, { w: 160, q: 75 }) ?? listing.image_url}
              alt={listing.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[#d0b8df] dark:text-[#503864]">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate min-w-0 flex-1 group-hover:text-[#503864] dark:group-hover:text-[#b58fcc] transition-colors">
              {listing.title}
            </h3>
            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              <span className="capitalize">{listing.status}</span>
            </span>
          </div>

          <p className="mt-1.5 text-lg font-bold text-[#503864] dark:text-[#b58fcc]">
            {formattedPrice}
          </p>

          <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
              {listing.category}
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              {formattedDate}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
